import React, { useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { DatePicker } from '@material-ui/pickers'

import { makeStyles } from '@material-ui/styles'

import { createMuiTheme } from '@material-ui/core/styles'
import { colors } from '@material-ui/core'
import { ThemeProvider } from '@material-ui/styles'
import { add, getISODay } from 'date-fns'

const white = '#FFFFFF'
const customTheme = createMuiTheme({
  palette: {
    primary: {
      contrastText: '#002f3d',
      dark: '#003366',
      main: '#5adae9',
      light: '#0072e5'
    },
    secondary: {
      contrastText: white,
      dark: colors.blue[900],
      main: colors.blue['A400'],
      light: colors.blue['A400']
    }
  },
  typography: {
    body1: {
      fontSize: '16px',
      letterSpacing: '-0.05px',
      lineHeight: '21px'
    },
    caption: {
      fontSize: '12px'
    },
    body2: {
      fontSize: '14px',
      letterSpacing: '-0.04px',
      lineHeight: '18px'
    }
  }
})

const getNextMonday = () => {
  const today = new Date()
  const offsetDays = 7 - getISODay(today) + 1 // 1 means Monday
  return add(today, { days: offsetDays })
}

const DefaultInput = React.forwardRef(
  ({ onClick, value, onKeyDown, InputProps, inputProps }, ref) => {
    return (
      <input
        ref={ref}
        onClick={onClick}
        value={value}
        onKeyDown={onKeyDown}
        {...InputProps}
        {...inputProps}></input>
    )
  }
)

function throttle(fn, wait) {
  var time = Date.now()
  return function () {
    if (time + wait - Date.now() < 0) {
      fn()
      time = Date.now()
    }
  }
}

function debounce(func, wait, immediate) {
  var timeout

  // This is the function that is actually executed when
  // the DOM event is triggered.
  return function executedFunction() {
    // Store the context of this and any
    // parameters passed to executedFunction
    var context = this
    var args = arguments

    // The function to be called after
    // the debounce time has elapsed
    var later = function () {
      // null timeout to indicate the debounce ended
      timeout = null

      // Call function now if you did not on the leading end
      if (!immediate) func.apply(context, args)
    }

    // Determine if you should call the function
    // on the leading or trail end
    var callNow = immediate && !timeout

    // This will reset the waiting every function execution.
    // This is the step that prevents the function from
    // being executed because it will never reach the
    // inside of the previous setTimeout
    clearTimeout(timeout)

    // Restart the debounce waiting period.
    // setTimeout returns a truthy value (it differs in web vs node)
    timeout = setTimeout(later, wait)

    // Call immediately if you're dong a leading
    // end execution
    if (callNow) func.apply(context, args)
  }
}

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    overflow: 'unset',
    border: '1px solid #668c98',
    borderRadius: 2,
    '&::before': {
      content: "''",
      display: 'block',
      width: '14px',
      height: '14px',
      position: 'absolute',
      border: 'solid #668c98',
      borderWidth: '1px 0 0 1px',
      backgroundColor: '#fff',
      transform: 'rotate(45deg)',
      top: '-8px',
      left: '20px'
    }
  }
}))

const localeFormatMap = {
  fi: 'EEE dd MMM',
  da: 'EEE, dd MMM',
  no: 'EEE. dd MMM',
  sv: 'EEE, dd MMM',
  en: 'EEE, dd MMM'
}

const BookameetingDatePicker = ({
  inputProps,
  placeholder,
  value,
  onChange
}) => {
  const currentLangcode = useSelector((state) => state.language.langcode)
  const [rectTop, setRectTop] = useState(0)
  const [rectLeft, setRectLeft] = useState(0)

  const classes = useStyles()

  const inputRef = useRef()

  React.useLayoutEffect(() => {
    function updatePosition() {
      if (inputRef.current) {
        setRectTop(inputRef.current.getBoundingClientRect().top)
        setRectLeft(inputRef.current.getBoundingClientRect().left)
      }
    }

    const debounceUpdatePosition = debounce(updatePosition, 500)

    window.addEventListener('resize', debounceUpdatePosition)
    window.addEventListener('scroll', debounceUpdatePosition)
    debounceUpdatePosition()
    return () => {
      window.removeEventListener('resize', debounceUpdatePosition)
      window.removeEventListener('scroll', debounceUpdatePosition)
    }
  }, [])

  return (
    <ThemeProvider theme={customTheme}>
      <DatePicker
        format={localeFormatMap[currentLangcode]}
        disableToolbar
        variant="inline"
        TextFieldComponent={(props) => {
          return (
            <DefaultInput
              {...props}
              placeholder={placeholder}
              inputProps={inputProps}
              ref={inputRef}
            />
          )
        }}
        value={value}
        onChange={onChange}
        PopoverProps={{
          anchorReference: 'anchorPosition',
          anchorPosition: {
            top: rectTop + 75,
            left: rectLeft
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          PaperProps: {
            className: classes.paperContainer
          },
          transitionDuration: 0
        }}
        minDate={new Date()}
        autoOk
      />
    </ThemeProvider>
  )
}

export default BookameetingDatePicker
